/**
 * LEADS CONTEXT - STATE GLOBAL COMPARTILHADO
 * ==========================================
 * Gerencia state de leads de forma centralizada
 * Sincroniza automaticamente Lista, Kanban, Analytics e Automações
 */

import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import {
  authenticatedGet,
  authenticatedPost,
  authenticatedPut,
  authenticatedDelete,
} from '@/utils/api-helpers';
import { toast } from 'react-hot-toast';
import { useFirebaseAuth } from './FirebaseAuthContext';

export interface Lead {
  lead_id: string;
  user_id: string;
  name: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  property_type?: string;
  intent?: string;
  budget_min?: number;
  budget_max?: number;
  location?: string;
  status: string;
  temperature: 'hot' | 'warm' | 'cold';
  score: number;
  source?: string;
  notes?: string;
  last_contact_at?: string;
  next_followup_at?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  priority?: string;
  tags?: string;
  exported?: number;
  atendimento_tipo?: 'bot' | 'humano';
  atendente_id?: string;
}

export interface DashboardStats {
  total_leads: number;
  hot_leads: number;
  warm_leads: number;
  cold_leads: number;
  new_leads: number;
  contacted_leads: number;
  qualified_leads: number;
  exported_leads: number;
  avg_score: number;
}

export interface UserLimits {
  leads: {
    used: number;
    limit: number;
    remaining: number;
    percentage: number;
  };
  exports: {
    used: number;
    limit: number;
    remaining: number;
  };
}

export interface LeadsFilters {
  temperature?: 'hot' | 'warm' | 'cold';
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

interface LeadsContextType {
  // State
  leads: Lead[];
  stats: DashboardStats;
  limits: UserLimits;
  loading: boolean;
  exportingLeads: boolean;
  error: string | null;
  lastUpdate: Date | null;

  // Actions
  refreshLeads: (filters?: LeadsFilters) => Promise<void>;
  addLead: (lead: Lead) => void;
  updateLead: (leadId: string, updates: Partial<Lead>) => Promise<void>;
  deleteLead: (leadId: string) => Promise<void>;
  exportLeads: (leadIds?: string[]) => Promise<void>;

  // Real-time sync
  subscribeToUpdates: (callback: (lead: Lead) => void) => () => void;
  broadcastUpdate: (leadId: string, updates: Partial<Lead>) => void;
}

const LeadsContext = createContext<LeadsContextType | undefined>(undefined);

export function LeadsProvider({ children }: { children: React.ReactNode }) {
  // ✅ LAYER 0: Hook de autenticação (defense in depth)
  const { isAuthenticated } = useFirebaseAuth();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    total_leads: 0,
    hot_leads: 0,
    warm_leads: 0,
    cold_leads: 0,
    new_leads: 0,
    contacted_leads: 0,
    qualified_leads: 0,
    exported_leads: 0,
    avg_score: 0,
  });
  const [limits, setLimits] = useState<UserLimits>({
    leads: { used: 0, limit: 100, remaining: 100, percentage: 0 },
    exports: { used: 0, limit: 50, remaining: 50 },
  });
  const [loading, setLoading] = useState(false);
  const [exportingLeads, setExportingLeads] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Subscribers para broadcast interno
  const subscribersRef = useRef<Set<(lead: Lead) => void>>(new Set());

  // Polling interval ref
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Guard contra fetches concorrentes (evita double-fetch em StrictMode e polling simultâneo)
  const isRefreshingRef = useRef(false);

  // Cache local com LocalStorage
  const CACHE_KEY = 'organizador_leads_cache';
  const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutos

  // Carregar cache ao montar
  useEffect(() => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_EXPIRY) {
          setLeads(data.leads || []);
          setStats(data.stats || stats);
          setLimits(data.limits || limits);
          setLastUpdate(new Date(timestamp));
        }
      } catch (e) {
        console.error('Error loading cache:', e);
      }
    }
  }, []);

  // Salvar cache quando dados mudam
  useEffect(() => {
    if (leads.length > 0) {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: { leads, stats, limits },
          timestamp: Date.now(),
        })
      );
    }
  }, [leads, stats, limits]);

  // Calcular stats automaticamente
  const calculateStats = useCallback((leadsData: Lead[]): DashboardStats => {
    return {
      total_leads: leadsData.length,
      hot_leads: leadsData.filter(l => l.temperature === 'hot').length,
      warm_leads: leadsData.filter(l => l.temperature === 'warm').length,
      cold_leads: leadsData.filter(l => l.temperature === 'cold').length,
      new_leads: leadsData.filter(l => l.status === 'new').length,
      contacted_leads: leadsData.filter(l => l.status === 'contacted').length,
      qualified_leads: leadsData.filter(l => l.status === 'qualified').length,
      exported_leads: leadsData.filter(l => l.exported === 1).length,
      avg_score:
        leadsData.length > 0
          ? leadsData.reduce((sum, l) => sum + l.score, 0) / leadsData.length
          : 0,
    };
  }, []);

  // Refresh completo dos leads
  const refreshLeads = useCallback(
    async (filters?: LeadsFilters) => {
      // ✅ LAYER 2: Guard - skip se não autenticado
      if (!isAuthenticated()) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[LeadsContext] Skip refresh - usuário não autenticado');
        }
        return;
      }

      // Guard contra fetches concorrentes
      if (isRefreshingRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[LeadsContext] Skip refresh - já em andamento');
        }
        return;
      }

      isRefreshingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        // Build query string from filters
        const params = new URLSearchParams();
        if (filters?.temperature) params.append('temperature', filters.temperature);
        if (filters?.status) params.append('status', filters.status);
        if (filters?.search) params.append('search', filters.search);
        if (filters?.limit !== undefined) params.append('limit', String(filters.limit));
        if (filters?.offset !== undefined) params.append('offset', String(filters.offset));

        const queryString = params.toString();
        const leadsUrl = `/api/organizador-clientes/leads${queryString ? `?${queryString}` : ''}`;

        // Carregar leads e dashboard em paralelo
        const [leadsRes, dashboardRes] = await Promise.all([
          authenticatedGet(leadsUrl),
          authenticatedGet('/api/organizador-clientes/dashboard'),
        ]);

        if (leadsRes.ok) {
          const leadsData = await leadsRes.json();
          const newLeads = leadsData.leads || [];
          setLeads(newLeads);

          // Calcular stats
          const newStats = calculateStats(newLeads);
          setStats(newStats);

          setLastUpdate(new Date());
        } else {
          throw new Error('Failed to load leads');
        }

        if (dashboardRes.ok) {
          const dashboardData = await dashboardRes.json();

          // Atualizar limites
          const limitsData = dashboardData.limits || {};
          setLimits({
            leads: {
              used: Number(limitsData.monthly_leads) || 0,
              limit: Number(limitsData.max_monthly_leads) || 100,
              remaining: Number(limitsData.remaining_leads) || 100,
              percentage:
                limitsData.monthly_leads && limitsData.max_monthly_leads
                  ? Math.round((limitsData.monthly_leads / limitsData.max_monthly_leads) * 100)
                  : 0,
            },
            exports: {
              used: Number(limitsData.exports_used) || 0,
              limit: Number(limitsData.max_exports) || 50,
              remaining: Number(limitsData.max_exports) - Number(limitsData.exports_used) || 50,
            },
          });
        }
      } catch (err: any) {
        console.error('Error refreshing leads:', err);
        setError(err.message || 'Failed to load leads');
        toast.error('Erro ao carregar leads');
      } finally {
        setLoading(false);
        isRefreshingRef.current = false;
      }
    },
    [calculateStats, isAuthenticated]
  );

  // Adicionar lead (otimista)
  const addLead = useCallback(
    (lead: Lead) => {
      setLeads(prev => [lead, ...prev]);
      setStats(prev => calculateStats([lead, ...leads]));
      setLastUpdate(new Date());

      // Broadcast para subscribers
      subscribersRef.current.forEach(callback => callback(lead));
    },
    [leads, calculateStats]
  );

  // Atualizar lead (otimista com rollback)
  const updateLead = useCallback(
    async (leadId: string, updates: Partial<Lead>) => {
      let previousLeads: Lead[] = [];
      let updatedLeads: Lead[] = [];

      // Update otimista com acesso ao estado atual
      setLeads(prev => {
        previousLeads = [...prev]; // Salvar backup do estado atual
        const newLeads = prev.map(lead =>
          lead.lead_id === leadId
            ? { ...lead, ...updates, updated_at: new Date().toISOString() }
            : lead
        );
        updatedLeads = newLeads; // Salvar referência aos leads atualizados
        return newLeads;
      });

      // Atualizar stats baseado nos leads atualizados
      setStats(calculateStats(updatedLeads));
      setLastUpdate(new Date());

      // Broadcast
      const updatedLead = updatedLeads.find(l => l.lead_id === leadId);
      if (updatedLead) {
        subscribersRef.current.forEach(callback => callback(updatedLead));
      }

      try {
        // Persistir no backend
        const res = await authenticatedPut(`/api/organizador-clientes/leads/${leadId}`, updates);

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to update lead');
        }

        // Silenciosamente - não mostrar toast em cada mudança do Kanban
        if (process.env.NODE_ENV === 'development') {
          console.log('[LeadsContext] Lead updated successfully:', leadId);
        }
      } catch (err: any) {
        console.error('Error updating lead:', err);

        // Rollback em caso de erro
        setLeads(previousLeads);
        setStats(calculateStats(previousLeads));

        toast.error('Erro ao atualizar lead. Revertendo mudanças.');
      }
    },
    [calculateStats]
  );

  // Deletar lead
  const deleteLead = useCallback(
    async (leadId: string) => {
      const previousLeads = [...leads];

      // Update otimista
      setLeads(prev => prev.filter(l => l.lead_id !== leadId));
      const newLeads = leads.filter(l => l.lead_id !== leadId);
      setStats(calculateStats(newLeads));
      setLastUpdate(new Date());

      try {
        const res = await authenticatedDelete(`/api/organizador-clientes/leads/${leadId}`);

        if (!res.ok) {
          throw new Error('Failed to delete lead');
        }

        toast.success('Lead removido com sucesso!');
      } catch (err: any) {
        console.error('Error deleting lead:', err);

        // Rollback
        setLeads(previousLeads);
        setStats(calculateStats(previousLeads));

        toast.error('Erro ao remover lead. Revertendo.');
      }
    },
    [leads, calculateStats]
  );

  // Exportar leads
  const exportLeads = useCallback(
    async (leadIds?: string[]) => {
      setExportingLeads(true);
      const toastId = toast.loading('Exportando contatos...');

      try {
        const res = await authenticatedPost('/api/organizador-clientes/leads/export/whatsapp', {
          leadIds: leadIds || [],
        });

        const data = await res.json();

        if (data.success) {
          toast.success(`✅ ${data.total} contatos exportados!`, { id: toastId });

          // Atualizar flag exported
          if (leadIds && leadIds.length > 0) {
            setLeads(prev =>
              prev.map(lead => (leadIds.includes(lead.lead_id) ? { ...lead, exported: 1 } : lead))
            );
          }

          // Refresh para atualizar contadores
          refreshLeads();
        } else {
          toast.error(data.message || 'Erro ao exportar', { id: toastId });
        }
      } catch (err: any) {
        console.error('Error exporting leads:', err);
        toast.error('Erro ao exportar contatos', { id: toastId });
      } finally {
        setExportingLeads(false);
      }
    },
    [refreshLeads]
  );

  // Subscribe para updates em tempo real
  const subscribeToUpdates = useCallback((callback: (lead: Lead) => void) => {
    subscribersRef.current.add(callback);

    // Return unsubscribe function
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  // Broadcast update para todos os subscribers
  const broadcastUpdate = useCallback(
    (leadId: string, updates: Partial<Lead>) => {
      const lead = leads.find(l => l.lead_id === leadId);
      if (lead) {
        const updatedLead = { ...lead, ...updates };
        subscribersRef.current.forEach(callback => callback(updatedLead));
      }
    },
    [leads]
  );

  // Polling como fallback (5 minutos - reduzido para economizar requests)
  useEffect(() => {
    // ✅ LAYER 1: Guard - só inicia polling se autenticado
    if (!isAuthenticated()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[LeadsContext] Polling desabilitado - usuário não autenticado');
      }
      return;
    }

    pollingIntervalRef.current = setInterval(() => {
      if (!document.hidden && !loading) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[LeadsContext] Polling refresh (5min interval)...');
        }
        refreshLeads().catch(err => {
          console.error('[LeadsContext] Polling error:', err);
          // Não fazer nada - erro já tratado em refreshLeads
        });
      }
    }, 300000) as any; // 5 minutos (300s) - reduz carga no servidor

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [refreshLeads, loading, isAuthenticated]);

  // Refresh quando página fica visível
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && lastUpdate) {
        const timeSinceUpdate = Date.now() - lastUpdate.getTime();
        if (timeSinceUpdate > 60000) {
          // Mais de 1 minuto
          if (process.env.NODE_ENV === 'development') {
            console.log('[LeadsContext] Page visible, refreshing stale data...');
          }
          refreshLeads();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshLeads, lastUpdate]);

  // ✅ LAYER 3: Limpar estado quando usuário fizer logout
  useEffect(() => {
    if (!isAuthenticated()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[LeadsContext] Limpando estado - usuário desconectado');
      }
      setLeads([]);
      setStats({
        total_leads: 0,
        hot_leads: 0,
        warm_leads: 0,
        cold_leads: 0,
        new_leads: 0,
        contacted_leads: 0,
        qualified_leads: 0,
        exported_leads: 0,
        avg_score: 0,
      });
      setLimits({
        leads: { used: 0, limit: 100, remaining: 100, percentage: 0 },
        exports: { used: 0, limit: 50, remaining: 50 },
      });
      setError(null);
      setLastUpdate(null);

      // Limpar cache do localStorage
      localStorage.removeItem('organizador_leads_cache');
    }
  }, [isAuthenticated]);

  const value: LeadsContextType = {
    leads,
    stats,
    limits,
    loading,
    exportingLeads,
    error,
    lastUpdate,
    refreshLeads,
    addLead,
    updateLead,
    deleteLead,
    exportLeads,
    subscribeToUpdates,
    broadcastUpdate,
  };

  return <LeadsContext.Provider value={value}>{children}</LeadsContext.Provider>;
}

export function useLeads() {
  const context = useContext(LeadsContext);
  if (!context) {
    throw new Error('useLeads must be used within LeadsProvider');
  }
  return context;
}
